import {
  TABLE_Z_INDEX,
  pinnedColumnShadowStyles,
  pinnedColumnShadowStylesLeft
} from '@hconnect/uikit'
import {useTheme} from '@mui/material'
import {useCallback} from 'react'

type StickyConfig = {
  left?: number | string
  right?: number | string
  width: number
}

type CellStyleConfig<TKey extends string> = {
  stickyLeft: Partial<Record<TKey, StickyConfig>>
  stickyRight: Partial<Record<TKey, StickyConfig>>
  defaultWidth: number
  scrollingRight: boolean
  scrollingLeft: boolean
  backgroundColor: string
  hoverBackgroundColor: string
}

export const useTableCellStyle = <TKey extends string>({
  stickyLeft,
  stickyRight,
  defaultWidth,
  scrollingRight,
  scrollingLeft,
  backgroundColor,
  hoverBackgroundColor
}: CellStyleConfig<TKey>) => {
  const theme = useTheme()
  const maxRightValue = Object.values(stickyRight).reduce<number>(
    (max, config) => Math.max(max, Number((config as StickyConfig)?.right ?? 0)),
    0
  )

  return useCallback(
    (key: string) => {
      const columnKey = key as TKey
      const leftConfig = stickyLeft[columnKey]
      const rightConfig = stickyRight[columnKey]

      // Frozen (sticky) cells must stay fully opaque so horizontally scrolled cells never
      // show through them on hover. The hover color is translucent, so paint it as an overlay
      // on top of the opaque background instead of replacing the background color.
      const hoverStyle = {
        '.MuiTableRow-root:hover &': {
          backgroundImage: `linear-gradient(${hoverBackgroundColor}, ${hoverBackgroundColor})`
        }
      }

      if (leftConfig) {
        const style: Record<string, unknown> = {
          position: 'sticky',
          zIndex: TABLE_Z_INDEX.pinnedBodyCell,
          backgroundColor,
          ...leftConfig,
          ...hoverStyle
        }
        if (scrollingRight) style['&::after'] = pinnedColumnShadowStyles(theme)
        return style
      }

      if (rightConfig) {
        const style: Record<string, unknown> = {
          position: 'sticky',
          zIndex: TABLE_Z_INDEX.pinnedBodyCell,
          backgroundColor,
          ...rightConfig,
          ...hoverStyle
        }
        const isFirstRightStickyColumn = Number(rightConfig.right ?? 0) === maxRightValue
        if (scrollingLeft && isFirstRightStickyColumn)
          style['&::before'] = pinnedColumnShadowStylesLeft(theme)
        return style
      }

      return {width: defaultWidth, height: 56}
    },
    [
      stickyLeft,
      stickyRight,
      defaultWidth,
      scrollingRight,
      scrollingLeft,
      backgroundColor,
      hoverBackgroundColor,
      maxRightValue,
      theme
    ]
  )
}
